package org.zq.astrock_ai.ui.research.gushi.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import org.zq.astrock_ai.ui.research.gushi.StockColors
import org.zq.astrock_ai.ui.research.gushi.adaptiveDialogWidth
import org.zq.astrock_ai.ui.research.gushi.data.PaperSymbolSummary
import org.zq.astrock_ai.ui.research.gushi.data.PaperTradeEntry
import org.zq.astrock_ai.ui.research.gushi.data.PaperTradeStore
import org.zq.astrock_ai.ui.research.gushi.data.formatScanHistoryTime
import org.zq.astrock_ai.ui.research.gushi.data.summarizePaperTrades
import org.zq.astrock_ai.util.formatDecimal

private val LOT_OPTIONS = listOf(100, 200, 500, 1000)

@Composable
fun PaperTradeDialog(
    entries: List<PaperTradeEntry>,
    onDismiss: () -> Unit,
    onSelectCode: (String) -> Unit = {},
    onApplyToHoldings: ((List<PaperTradeEntry>, Int) -> Unit)? = null,
    onExportSummary: ((List<PaperSymbolSummary>, Int) -> Unit)? = null,
    defaultCode: String = "",
    defaultName: String = "",
    onEntriesChanged: () -> Unit = {},
) {
    val shape = RoundedCornerShape(14.dp)
    var appliedKeys by remember { mutableStateOf(setOf<String>()) }
    var lotSize by remember { mutableStateOf(100) }
    var showSummary by remember { mutableStateOf(false) }
    var showManual by remember { mutableStateOf(false) }
    var noteDraft by remember { mutableStateOf("") }
    var sideBuy by remember { mutableStateOf(true) }
    var priceDraft by remember { mutableStateOf("") }
    var editingNoteKey by remember { mutableStateOf<String?>(null) }
    var editingNoteText by remember { mutableStateOf("") }
    val fieldColors = OutlinedTextFieldDefaults.colors(
        focusedBorderColor = StockColors.PrimaryBlue,
        unfocusedBorderColor = StockColors.Border,
        focusedTextColor = StockColors.TextPrimary,
        unfocusedTextColor = StockColors.TextPrimary,
    )
    val applicable = remember(entries, appliedKeys) {
        entries.filter { it.price > 0 && "${it.atMs}-${it.code}-${it.side}" !in appliedKeys }
    }
    val summaries = remember(entries, lotSize) { summarizePaperTrades(entries, lotSize) }
    Dialog(onDismissRequest = onDismiss) {
        Column(
            modifier = Modifier
                .adaptiveDialogWidth(480.dp)
                .heightIn(max = 600.dp)
                .background(Color.White, shape)
                .border(BorderStroke(1.dp, StockColors.Border), shape)
                .padding(18.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    when {
                        showManual -> "记一笔"
                        showSummary -> "纸面汇总"
                        else -> "纸面流水"
                    },
                    color = StockColors.TextPrimary,
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    modifier = Modifier.weight(1f),
                )
                if (!showManual) {
                    Text(
                        if (showSummary) "流水" else "汇总",
                        color = StockColors.PrimaryBlue,
                        fontSize = 13.sp,
                        fontWeight = FontWeight.SemiBold,
                        modifier = Modifier
                            .background(Color(0xFFE8F0FE), RoundedCornerShape(10.dp))
                            .clickable { showSummary = !showSummary }
                            .padding(horizontal = 10.dp, vertical = 5.dp),
                    )
                    Spacer(Modifier.width(6.dp))
                    Text(
                        "记一笔",
                        color = StockColors.PrimaryBlue,
                        fontSize = 13.sp,
                        fontWeight = FontWeight.SemiBold,
                        modifier = Modifier
                            .background(Color(0xFFE8F0FE), RoundedCornerShape(10.dp))
                            .clickable {
                                showManual = true
                                showSummary = false
                            }
                            .padding(horizontal = 10.dp, vertical = 5.dp),
                    )
                    Spacer(Modifier.width(6.dp))
                }
                Text(
                    if (showManual) "返回" else "关闭",
                    color = StockColors.PrimaryBlue,
                    fontSize = 13.sp,
                    modifier = Modifier
                        .clickable {
                            if (showManual) showManual = false else onDismiss()
                        }
                        .padding(6.dp),
                )
            }
            Spacer(Modifier.height(6.dp))
            if (showManual) {
                Text(
                    "当前 ${defaultName.ifBlank { defaultCode.ifBlank { "--" } }} · 手数 $lotSize",
                    color = StockColors.TextSecondary,
                    fontSize = 11.sp,
                )
                Spacer(Modifier.height(8.dp))
                Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    LOT_OPTIONS.forEach { lot ->
                        val selected = lotSize == lot
                        Text(
                            "$lot",
                            color = if (selected) Color.White else StockColors.TextSecondary,
                            fontSize = 12.sp,
                            modifier = Modifier
                                .background(
                                    if (selected) StockColors.PrimaryBlue else Color(0xFFF3F4F6),
                                    RoundedCornerShape(10.dp),
                                )
                                .clickable { lotSize = lot }
                                .padding(horizontal = 8.dp, vertical = 4.dp),
                        )
                    }
                    Spacer(Modifier.weight(1f))
                    Text(
                        if (sideBuy) "买" else "卖",
                        color = Color.White,
                        fontSize = 12.sp,
                        modifier = Modifier
                            .background(
                                if (sideBuy) StockColors.UpRed else StockColors.DownGreen,
                                RoundedCornerShape(10.dp),
                            )
                            .clickable { sideBuy = !sideBuy }
                            .padding(horizontal = 12.dp, vertical = 4.dp),
                    )
                }
                Spacer(Modifier.height(8.dp))
                OutlinedTextField(
                    value = priceDraft,
                    onValueChange = { priceDraft = it.filter { ch -> ch.isDigit() || ch == '.' }.take(12) },
                    singleLine = true,
                    label = { Text("价格") },
                    colors = fieldColors,
                    modifier = Modifier.fillMaxWidth(),
                )
                Spacer(Modifier.height(6.dp))
                OutlinedTextField(
                    value = noteDraft,
                    onValueChange = { noteDraft = it.take(40) },
                    singleLine = true,
                    label = { Text("备注（可选）") },
                    colors = fieldColors,
                    modifier = Modifier.fillMaxWidth(),
                )
                Spacer(Modifier.height(12.dp))
                Text(
                    "保存",
                    color = Color.White,
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier
                        .background(
                            if (defaultCode.length == 6 && (priceDraft.toDoubleOrNull() ?: 0.0) > 0) {
                                StockColors.PrimaryBlue
                            } else {
                                Color(0xFF9CA3AF)
                            },
                            RoundedCornerShape(10.dp),
                        )
                        .clickable(
                            enabled = defaultCode.length == 6 && (priceDraft.toDoubleOrNull() ?: 0.0) > 0,
                        ) {
                            PaperTradeStore.appendSignal(
                                code = defaultCode,
                                name = defaultName,
                                side = if (sideBuy) "BUY" else "SELL",
                                price = priceDraft.toDoubleOrNull() ?: return@clickable,
                                reason = "手动",
                                source = "manual",
                                quantity = lotSize,
                                note = noteDraft,
                            )
                            onEntriesChanged()
                            noteDraft = ""
                            priceDraft = ""
                            showManual = false
                        }
                        .padding(horizontal = 16.dp, vertical = 8.dp),
                )
                return@Column
            }
            Text(
                if (showSummary) {
                    "FIFO 按各笔手数配对；无手数时回退 $lotSize 股"
                } else {
                    "支持手数与备注；入仓优先用记录手数"
                },
                color = StockColors.TextSecondary,
                fontSize = 11.sp,
            )
            Spacer(Modifier.height(8.dp))
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                Text("手数", color = StockColors.TextSecondary, fontSize = 12.sp)
                LOT_OPTIONS.forEach { lot ->
                    val selected = lotSize == lot
                    Text(
                        "$lot",
                        color = if (selected) Color.White else StockColors.TextSecondary,
                        fontSize = 12.sp,
                        fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
                        modifier = Modifier
                            .background(
                                if (selected) StockColors.PrimaryBlue else Color(0xFFF3F4F6),
                                RoundedCornerShape(10.dp),
                            )
                            .clickable { lotSize = lot }
                            .padding(horizontal = 8.dp, vertical = 4.dp),
                    )
                }
                Spacer(Modifier.weight(1f))
                if (showSummary) {
                    if (onExportSummary != null && summaries.isNotEmpty()) {
                        Text(
                            "导出",
                            color = Color.White,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                            modifier = Modifier
                                .background(StockColors.PrimaryBlue, RoundedCornerShape(10.dp))
                                .clickable { onExportSummary(summaries, lotSize) }
                                .padding(horizontal = 10.dp, vertical = 5.dp),
                        )
                    }
                } else if (onApplyToHoldings != null && applicable.isNotEmpty()) {
                    Text(
                        "批量入仓 ${applicable.size}",
                        color = Color.White,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.SemiBold,
                        modifier = Modifier
                            .background(StockColors.PrimaryBlue, RoundedCornerShape(10.dp))
                            .clickable {
                                onApplyToHoldings(applicable, lotSize)
                                appliedKeys = appliedKeys + applicable.map { "${it.atMs}-${it.code}-${it.side}" }
                            }
                            .padding(horizontal = 10.dp, vertical = 5.dp),
                    )
                }
            }
            Spacer(Modifier.height(12.dp))
            if (showSummary) {
                if (summaries.isEmpty()) {
                    Text("暂无有效买卖价可汇总", color = StockColors.TextSecondary, fontSize = 13.sp)
                } else {
                    val totalPnl = summaries.sumOf { it.realizedPnl }
                    Text(
                        "合计已实现 ${formatDecimal(totalPnl, forceSign = true)}",
                        color = if (totalPnl >= 0) StockColors.UpRed else StockColors.DownGreen,
                        fontWeight = FontWeight.SemiBold,
                        fontSize = 13.sp,
                    )
                    Spacer(Modifier.height(8.dp))
                    LazyColumn(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        items(summaries, key = { it.code }) { s ->
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .background(Color(0xFFF8F9FB), RoundedCornerShape(10.dp))
                                    .clickable { onSelectCode(s.code) }
                                    .padding(12.dp),
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Text(
                                        "${s.name} · ${s.code}",
                                        color = StockColors.TextPrimary,
                                        fontWeight = FontWeight.SemiBold,
                                        fontSize = 13.sp,
                                        modifier = Modifier.weight(1f),
                                    )
                                    Text(
                                        formatDecimal(s.realizedPnl, forceSign = true),
                                        color = if (s.realizedPnl >= 0) StockColors.UpRed else StockColors.DownGreen,
                                        fontWeight = FontWeight.Bold,
                                        fontSize = 13.sp,
                                    )
                                }
                                Spacer(Modifier.height(3.dp))
                                Text(
                                    "买${s.buySignals}/卖${s.sellSignals} · 配对${s.matchedShares}股 · 未平${s.openShares}" +
                                        " · 均买${formatDecimal(s.avgBuy, 3)} 均卖${formatDecimal(s.avgSell, 3)}",
                                    color = StockColors.TextSecondary,
                                    fontSize = 11.sp,
                                )
                            }
                        }
                    }
                }
            } else if (entries.isEmpty()) {
                Text("暂无记录", color = StockColors.TextSecondary, fontSize = 13.sp)
            } else {
                LazyColumn(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    items(entries, key = { "${it.atMs}-${it.code}-${it.side}" }) { e ->
                        val key = "${e.atMs}-${e.code}-${e.side}"
                        val color = if (e.side == "BUY") StockColors.UpRed else StockColors.DownGreen
                        val applied = key in appliedKeys
                        val applyQty = if (e.quantity > 0) e.quantity else lotSize
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(Color(0xFFF8F9FB), RoundedCornerShape(10.dp))
                                .padding(12.dp),
                        ) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Text(
                                    "${e.name.ifBlank { e.code }} · ${if (e.side == "BUY") "买" else "卖"}",
                                    color = color,
                                    fontWeight = FontWeight.SemiBold,
                                    fontSize = 13.sp,
                                    modifier = Modifier
                                        .weight(1f)
                                        .clickable { onSelectCode(e.code) },
                                )
                                Text(
                                    formatScanHistoryTime(e.atMs),
                                    color = StockColors.TextSecondary,
                                    fontSize = 11.sp,
                                )
                            }
                            Spacer(Modifier.height(3.dp))
                            Text(
                                "价 ${if (e.price > 0) formatDecimal(e.price) else "--"} · ${e.quantity}股 · ${e.source} · ${e.reason}",
                                color = StockColors.TextSecondary,
                                fontSize = 11.sp,
                                maxLines = 2,
                            )
                            if (editingNoteKey == key) {
                                Spacer(Modifier.height(6.dp))
                                OutlinedTextField(
                                    value = editingNoteText,
                                    onValueChange = { editingNoteText = it.take(40) },
                                    singleLine = true,
                                    label = { Text("备注") },
                                    colors = fieldColors,
                                    modifier = Modifier.fillMaxWidth(),
                                )
                                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                    Text(
                                        "保存备注",
                                        color = StockColors.PrimaryBlue,
                                        fontSize = 12.sp,
                                        modifier = Modifier.clickable {
                                            PaperTradeStore.updateNote(e.atMs, e.code, e.side, editingNoteText)
                                            onEntriesChanged()
                                            editingNoteKey = null
                                        },
                                    )
                                    Text(
                                        "取消",
                                        color = StockColors.TextSecondary,
                                        fontSize = 12.sp,
                                        modifier = Modifier.clickable { editingNoteKey = null },
                                    )
                                }
                            } else {
                                Spacer(Modifier.height(4.dp))
                                Text(
                                    if (e.note.isBlank()) "添加备注" else "备注：${e.note}",
                                    color = if (e.note.isBlank()) StockColors.PrimaryBlue else StockColors.TextSecondary,
                                    fontSize = 11.sp,
                                    modifier = Modifier.clickable {
                                        editingNoteKey = key
                                        editingNoteText = e.note
                                    },
                                )
                            }
                            if (onApplyToHoldings != null && e.price > 0) {
                                Spacer(Modifier.height(6.dp))
                                Text(
                                    if (applied) "已入仓" else "入仓 ${applyQty}股",
                                    color = if (applied) StockColors.TextSecondary else StockColors.PrimaryBlue,
                                    fontSize = 12.sp,
                                    fontWeight = FontWeight.SemiBold,
                                    modifier = Modifier
                                        .background(
                                            if (applied) Color(0xFFF3F4F6) else Color(0xFFE8F0FE),
                                            RoundedCornerShape(10.dp),
                                        )
                                        .clickable(enabled = !applied) {
                                            onApplyToHoldings(listOf(e), lotSize)
                                            appliedKeys = appliedKeys + key
                                        }
                                        .padding(horizontal = 10.dp, vertical = 4.dp),
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}
